import type { OrderDetails } from '../model/order.types'
import type { DisplayState } from '@/entities/state/model'
import { formatTime } from '@/shared/lib/dayjs'

/**
 * List of order statuses that are considered "On The Way"
 * Only these orders should show ETA/scheduled time information
 */
const ON_THE_WAY_STATUSES = ['OnTheWay', 'TempoOnTheWay', 'PartnerOnTheWay'] as const

/**
 * Determines if an order status is considered "On The Way"
 * @param orderStatus - The order status to check
 * @returns true if the order is in an "On The Way" state
 */
export function isOrderOnTheWay(orderStatus: string): boolean {
  return ON_THE_WAY_STATUSES.includes(orderStatus as any)
}

/**
 * ETA Display Business Logic
 *
 * getEtaDisplayText:
 *   - If isOnTheWay is true and both eta and etaDirection are present, display relative time like "Over 15 Mins" or "5 mins".
 *   - Else if isOnTheWay is true and scheduledTimeForCollection is present, treat as a scheduled/guessed ETA and display "Approx. h:mm A" (12-hour format with AM/PM prefix).
 *   - Else if either eta or etaDirection is present (but not both), use the orderStatus as fallback.
 *   - Else if displayState.stateDisplayName is present, use the user-friendly display name.
 *   - Else, display the orderStatus string as final fallback.
 *   - For non-OnTheWay orders, displayState.stateDisplayName is prioritized over orderStatus.
 *   - Only orders in "On The Way" status should show ETA/scheduled time information.
 *
 * formatEtaMinutes:
 *   - If eta > 15 minutes, display "Over 15 Mins".
 *   - If eta ≤ 15 minutes, display "{minutes} min(s)" (e.g., "5 mins", "1 min").
 *   - Used for showing countdowns or short ETAs.
 *
 * formatScheduledTime:
 *   - Formats a scheduled time (ISO string or HH:MM) as "h:mm A" (12-hour format with AM/PM).
 *   - Used for displaying scheduled/guessed ETAs.
 */
export function getEtaDisplayText({
  eta,
  etaDirection,
  scheduledTimeForCollection,
  orderStatus,
  isOnTheWay,
  displayState,
}: {
  eta?: number
  etaDirection?: string
  scheduledTimeForCollection?: string
  orderStatus: string
  isOnTheWay?: boolean
  displayState?: DisplayState
}): string {
  const orderIsOnTheWay = isOnTheWay !== undefined ? isOnTheWay : isOrderOnTheWay(orderStatus)

  // Only show ETA/scheduled time for orders that are "On The Way"
  if (orderIsOnTheWay) {
    // Priority: eta+etaDirection > scheduledTimeForCollection > incomplete ETA data (eta OR etaDirection) > displayState.stateDisplayName > orderStatus
    if (eta !== undefined && etaDirection) {
      return formatEtaMinutes(eta, etaDirection as OrderDetails['etaDirection'])
    } else if (scheduledTimeForCollection) {
      const scheduledTime = formatScheduledTime(scheduledTimeForCollection)
      return scheduledTime ? `Approx. ${scheduledTime}` : orderStatus
    } else if (eta !== undefined || etaDirection) {
      // If either eta or etaDirection is present (but not both), fall back to orderStatus
      return orderStatus
    } else if (displayState?.stateDisplayName) {
      return displayState.stateDisplayName
    }
  }

  // For non-OnTheWay orders, prioritize displayState.stateDisplayName over orderStatus
  if (displayState?.stateDisplayName) {
    return displayState.stateDisplayName
  }

  // Default fallback for all orders when no display state is available
  return orderStatus
}

/**
 * Formats ETA in minutes for display (e.g., "5 mins")
 * @param eta - ETA in seconds
 * @returns Formatted ETA string
 */
export function formatEtaMinutes(eta: OrderDetails['eta'], etaDirection: OrderDetails['etaDirection']): string {
  if (eta === undefined) {
    return ''
  }

  const minutes = Math.round(eta / 60)

  if ((etaDirection === 'greaterThan' && minutes === 15) || minutes > 15) {
    return 'Over 15 Mins'
  }

  const minuteText = minutes === 1 ? 'min' : 'mins'

  return `${minutes} ${minuteText}`
}

/**
 * Formats scheduledTimeForCollection into "h:mm A" format (12-hour with AM/PM)
 * @param scheduledTime - ISO string or time string for scheduled collection
 * @returns Formatted scheduled time string or empty string if invalid
 */
export function formatScheduledTime(scheduledTime?: string): string {
  if (!scheduledTime) {
    return ''
  }

  try {
    // Handle ISO date string
    const date = new Date(scheduledTime)
    if (!Number.isNaN(date.getTime())) {
      return formatTime(date, 'h:mm A')
    }

    // Handle HH:MM format directly - parse as today's date
    if (/^\d{2}:\d{2}$/.test(scheduledTime)) {
      const [hours, minutes] = scheduledTime.split(':').map(Number)

      // Validate hours and minutes are within valid ranges
      if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        return ''
      }

      const today = new Date()
      today.setHours(hours, minutes, 0, 0)
      return formatTime(today, 'h:mm A')
    }

    return ''
  } catch {
    return ''
  }
}
